/**
 * Helpers para `z.object(...).refine(...)` reutilizables entre
 * schemas que validan rangos de fechas ISO `YYYY-MM-DD`.
 *
 * Uso típico:
 * ```ts
 * z.object({
 *   fechaInicio: fechaIso,
 *   fechaFinEstimada: fechaIso,
 * }).refine(...finMayorOIgualInicio())
 * ```
 *
 * Acepta un mensaje custom para casos donde el copy depende del
 * contexto (paso 1 del wizard vs. subfase). El path siempre apunta
 * a `fechaFinEstimada` para que el form-error inline se muestre
 * pegado al campo correcto.
 */

export type FechaInicioFinPair = {
  fechaInicio: string;
  fechaFinEstimada: string;
};

const MENSAJE_DEFAULT = 'La fecha de fin debe ser igual o posterior a la fecha de inicio.';

/**
 * Core genérico de la regla "fin ≥ inicio" parametrizado por los **nombres de
 * campo**. Devuelve la tupla `[predicate, options]` para spread en `.refine(...)`,
 * apuntando el issue al campo de fin. Permite reusar la misma regla cuando los
 * campos no se llaman `fechaInicio`/`fechaFinEstimada` (p. ej. `faseInicio`/
 * `faseFin` en el paso de fases). Compara como string ISO `YYYY-MM-DD` (orden
 * lexicográfico ≡ cronológico).
 */
export function finNoAnteriorAInicio<I extends string, F extends string>(
  inicioKey: I,
  finKey: F,
  message: string = MENSAJE_DEFAULT,
): [(v: Record<I | F, string>) => boolean, { path: [F]; message: string }] {
  return [(v) => v[finKey] >= v[inicioKey], { path: [finKey], message }];
}

/**
 * Atajo de `finNoAnteriorAInicio` para el par estándar
 * `fechaInicio`/`fechaFinEstimada` (proyecto, subfase, form de proyecto).
 *
 * @param message Mensaje custom. Si se omite, usa el copy estándar.
 */
export function finMayorOIgualInicio(message: string = MENSAJE_DEFAULT) {
  return finNoAnteriorAInicio('fechaInicio', 'fechaFinEstimada', message);
}

/**
 * Core genérico de la regla "fin ≥ inicio" cuando **ambos campos son
 * opcionales**: solo valida si los dos vienen presentes. Es la contraparte de
 * `finNoAnteriorAInicio` para schemas de UPDATE y para filtros, donde un rango
 * a medio llenar (solo `desde`, solo `hasta`) es un estado legítimo.
 */
export function finNoAnteriorAInicioOpcional<I extends string, F extends string>(
  inicioKey: I,
  finKey: F,
  message: string = MENSAJE_DEFAULT,
): [
  // `?: string | undefined` explícito y no `Partial<…>`: con
  // `exactOptionalPropertyTypes` un `Partial` no admite la clave presente con
  // valor `undefined`, que es exactamente el caso que esta regla contempla.
  (v: { [K in I | F]?: string | undefined }) => boolean,
  { path: [F]; message: string },
] {
  return [
    (v) => {
      const inicio = v[inicioKey];
      const fin = v[finKey];
      return inicio === undefined || fin === undefined || fin >= inicio;
    },
    { path: [finKey], message },
  ];
}

/**
 * Variante para schemas de UPDATE donde ambas fechas son opcionales:
 * solo valida cuando ambas vienen presentes.
 */
export function finMayorOIgualInicioOpcional(message: string = MENSAJE_DEFAULT) {
  return finNoAnteriorAInicioOpcional('fechaInicio', 'fechaFinEstimada', message);
}
